import { Link } from 'react-router-dom'
import { ChevronLeft, Eye, Clock, MapPin, ExternalLink, ShieldCheck, ListChecks } from 'lucide-react'
import { useStormEscalation } from './useStormEscalation'
import { useCountdown } from './useCountdown'
import { SkyTint } from '@/features/home/SkyTint'
import { watchGuidance, watchHazardChips } from '@/lib/storm/copy'
import './storm.css'

/**
 * WATCHING — the amber, calm-authority elevated state. Auto-surfaced when a Severe
 * Thunderstorm / Tornado WATCH is active for the user's area. NOT the red takeover:
 * it explains what the watch means, what to watch for, and the hazards in play —
 * then gets out of the way. Reachable at /watching and via ?demo=watch.
 */
export function WatchingView() {
  const { saved, watch } = useStormEscalation()
  const expiryCountdown = useCountdown(watch?.ends ?? watch?.expires)

  // Full-bleed amber wash — the same layer the home transform uses, so home → watch
  // detail is one continuous amber state (neutral when there's nothing active).
  const sky = <SkyTint lat={saved?.lat ?? null} lon={saved?.lon ?? null} phase={watch ? 'watching' : 'quiet'} />

  if (!watch) {
    return (
      <>
        {sky}
        <div className="storm storm--clear watching">
          <header className="storm__bar">
            <Link to="/" className="storm__back" aria-label="Back home">
              <ChevronLeft size={20} />
            </Link>
            <div className="storm__title">
              <Eye size={16} /> <span>WATCHING</span>
            </div>
            <span />
          </header>
          <div className="storm__empty">
            <ShieldCheck size={34} />
            <p className="storm__empty-title">No active watch</p>
            <p className="storm__empty-sub">You’re all clear — nothing is brewing for your area right now.</p>
            <Link to="/" className="storm__exit">Back to home</Link>
          </div>
        </div>
      </>
    )
  }

  const guide = watchGuidance(watch)
  const chips = watchHazardChips(watch)

  return (
    <>
      {sky}
      <div className="storm watching" style={{ '--c': 'var(--status-watch)' } as React.CSSProperties}>
      <header className="storm__bar">
        <Link to="/" className="storm__back" aria-label="Back home">
          <ChevronLeft size={20} />
        </Link>
        <div className="storm__title">
          <Eye size={16} />
          <span>WATCHING</span>
        </div>
        {expiryCountdown && (
          <span className="storm__expiry">
            <Clock size={12} /> {expiryCountdown === 'expired' ? 'expired' : `expires ${expiryCountdown}`}
          </span>
        )}
      </header>

      <div className="storm__headline watching__headline">
        <h1 className="storm__event">{watch.event}</h1>
        <p className="storm__where">
          <MapPin size={13} /> {watch.areaDesc.split(';')[0]}
        </p>
      </div>

      <div className="watching__chips">
        {chips.map((c) => (
          <span key={c} className="watching__chip">
            {c}
          </span>
        ))}
      </div>

      <section className="watching__card">
        <h2 className="watching__card-title">What this means</h2>
        <p className="watching__meaning">{guide.meaning}</p>
      </section>

      <section className="watching__card">
        <h2 className="watching__card-title">
          <ListChecks size={15} /> What to watch for
        </h2>
        <ul className="watching__list">
          {guide.watchFor.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      </section>

      <p className="watching__assure">
        If a warning is issued for your location, Storm Mode opens automatically.
      </p>

      <details className="storm__full watching__full">
        <summary>
          <span>Full watch text</span>
          <ExternalLink size={14} />
        </summary>
        <div className="storm__full-body">
          {watch.headline && <p className="storm__full-instruction">{watch.headline}</p>}
          <p>{watch.description}</p>
          <p className="storm__full-sender">Issued by {watch.senderName}</p>
        </div>
      </details>

      <Link to="/" className="storm__exit watching__exit">Back to home</Link>
      </div>
    </>
  )
}
